import React, { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { getPhotoUrl } from "../../shared/utils";
import { isActuPourMoi } from "../../navigation/useAujourdhui";
import { useLues } from "../../navigation/actualitesLues";

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const dateCourte = (iso) => { const d = new Date(iso + 'T12:00:00'); return `${cap(d.toLocaleDateString('fr-FR', { weekday: 'short' }))} ${d.getDate()} ${d.toLocaleDateString('fr-FR', { month: 'short' })}`; };
const dansN = (n) => n === 0 ? "aujourd'hui" : n === 1 ? 'demain' : `dans ${n} jours`;
const texte = (html) => String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const Widget = ({ tone, icon, title, meta, children, foot, onFoot, testid }) => (
  <div className="opn-w" data-testid={testid}>
    <div className="opn-w__head"><span className={`opn-w__ico opn-w__ico--${tone}`} aria-hidden="true">{icon}</span><b>{title}</b>{meta && <span className="opn-w__meta">{meta}</span>}</div>
    {children}
    {foot && <button type="button" className={`opn-w__foot ${foot.hot ? 'opn-w__foot--hot' : ''}`} onClick={onFoot} data-testid={`${testid}-foot`}>{foot.label} →</button>}
  </div>
);

const ActusCard = ({ actus, user, onGoScreen }) => {
  const lues = useLues(user);
  const liste = useMemo(() => {
    const isMed = user?.role === 'Médecin';
    return [...actus].sort((a, b) => (isMed ? (isActuPourMoi(b, user.role) - isActuPourMoi(a, user.role)) : 0) || String(b.date_creation || '').localeCompare(String(a.date_creation || '')));
  }, [actus, user]);
  const nonLues = liste.filter(a => !lues.includes(a.id)).length;
  const [i, setI] = useState(0);
  useEffect(() => { const first = liste.findIndex(a => !lues.includes(a.id)); setI(first >= 0 ? first : 0); }, [liste, lues]);
  const a = liste[i];
  return (
    <Widget tone="blue" icon="📣" title="Actualités" meta={liste.length ? `${i + 1} / ${liste.length}` : null} testid="ajd-actus"
      foot={liste.length ? { label: nonLues > 0 ? `${nonLues} non lue${nonLues > 1 ? 's' : ''}` : 'Toutes les actualités' } : null} onFoot={() => onGoScreen('news')}>
      {!a ? <p className="opn-w__empty">Aucune actualité pour le moment.</p> : (
        <button type="button" className="opn-w__news" onClick={() => onGoScreen('news')} data-testid="ajd-actus-current">
          <b>{a.titre}{!lues.includes(a.id) && <i className="opn-w__dot-new" title="Non lue" />}</b>
          <span>{texte(a.contenu)}</span>
        </button>
      )}
      {liste.length > 1 && (
        <div className="opn-w__dots" data-testid="ajd-actus-dots">
          {liste.slice(0, 8).map((x, k) => <button key={x.id} type="button" className={k === i ? 'on' : ''} aria-label={`Actualité ${k + 1}`} onClick={() => setI(k)} />)}
        </div>
      )}
    </Widget>
  );
};

const GardesCard = ({ gardes, onGoScreen }) => {
  const [next, ...suite] = gardes;
  const mois = next ? cap(new Date(next.date + 'T12:00:00').toLocaleDateString('fr-FR', { month: 'long' })) : null;
  return (
    <Widget tone="red" icon="🚨" title="Gardes" meta={mois} testid="ajd-gardes" foot={{ label: 'Voir le planning' }} onFoot={() => onGoScreen('planning')}>
      {!next ? <p className="opn-w__empty">Aucune garde planifiée dans les 60 prochains jours.</p> : (
        <>
          <p className="opn-w__big" data-testid="ajd-garde-next">{dateCourte(next.date)}<small>Prochaine garde — {dansN(next.jours_restants ?? 0)}{next.note ? ` · ${next.note}` : ''}</small></p>
          {suite.length > 0 && <div className="opn-w__pills">{suite.map(g => <span key={g.id || g.date} className="opn-w__pill" title={g.note || ''} data-testid={`ajd-garde-${g.date}`}>{dateCourte(g.date).replace(/ [^ ]+$/, '')}</span>)}</div>}
        </>
      )}
    </Widget>
  );
};

const TodoCard = ({ todo, foot, onGoScreen }) => {
  const lignes = todo.slice(0, 4);
  const reste = todo.length - lignes.length;
  const pied = foot || (reste > 0 ? { label: `${reste} autre${reste > 1 ? 's' : ''} sujet${reste > 1 ? 's' : ''}`, screen: 'news' } : null);
  return (
    <Widget tone="green" icon="✅" title="À faire" meta={todo.length ? `${todo.length} sujet${todo.length > 1 ? 's' : ''}` : null} testid="ajd-todo" foot={pied} onFoot={() => onGoScreen(pied.screen)}>
      {!lignes.length ? <p className="opn-w__empty">Rien en attente de votre côté.</p> : (
        <div className="opn-w__list">
          {lignes.map(l => (
            <button key={l.id} type="button" className="opn-w__row" onClick={() => onGoScreen(l.screen, l.ecran)} data-testid={`ajd-todo-${l.id}`}>
              <em aria-hidden="true">{l.icon}</em><span>{l.label}</span><strong className={l.hot ? 'hot' : ''}>{l.n}{l.suffix ? <small> {l.suffix}</small> : null}</strong>
            </button>
          ))}
        </div>
      )}
    </Widget>
  );
};

const Anniversaires = ({ liste }) => liste.length > 0 && (
  <div className="opn-bdays" data-testid="ajd-anniversaires">
    <span className="opn-bdays__lbl">🎂 Anniversaires</span>
    {liste.slice(0, 8).map(a => (
      <span key={a.id} className="opn-bday" data-testid={`ajd-bday-${a.id}`}>
        <Avatar className="h-5 w-5">{a.photo_url && <AvatarImage src={getPhotoUrl(a.photo_url)} alt="" />}<AvatarFallback className="text-[9px]">{a.prenom?.[0]}{a.nom?.[0]}</AvatarFallback></Avatar>
        <b>{a.prenom}</b><span>{a.jours_restants === 0 ? "Aujourd'hui !" : a.jours_restants === 1 ? 'Demain' : `Dans ${a.jours_restants}j`}</span>
      </span>
    ))}
  </div>
);

const AujourdhuiBand = ({ data, user, onGoScreen }) => (
  <section className="opn-ajd" data-testid="ajd-band">
    <h2 className="opn-home__h2">Aujourd'hui au cabinet. <span>Ce qui bouge, ce qui vous attend.</span></h2>
    <div className={`opn-band ${data.loading ? 'opn-band--loading' : ''}`}>
      <ActusCard actus={data.actus} user={user} onGoScreen={onGoScreen} />
      <GardesCard gardes={data.gardes} onGoScreen={onGoScreen} />
      <TodoCard todo={data.todo} foot={data.foot} onGoScreen={onGoScreen} />
    </div>
    <Anniversaires liste={data.anniversaires} />
  </section>
);

export default AujourdhuiBand;
